import { useState, useEffect } from 'react';

import { useGetCaseSensitiveEmailLazyQuery, useGetWelcomeUnauthorizedUserLazyQuery } from '../__generated__/generated_types';

export const useValidEmail = (initialValue: string) => {
    const [email, setEmail] = useState(initialValue);
    const [emailIsValid, setEmailIsValid] = useState(true);

    useEffect(() => {
        if (email.length === 0) {
            setEmailIsValid(true);
            return;
        }

        // Validate the email against a regex.
        // This doesn't matter as we don't allow signup anyways.
        if(!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) {
            setEmailIsValid(false);
            return;
        }
        setEmailIsValid(true);
    }, [email]);

    return { email, setEmail, emailIsValid };
};

export const useValidPassword = (initialValue: string) => {
    const [password, setPassword] = useState(initialValue);
    const [passwordIsValid, setPasswordIsValid] = useState(true);

    useEffect(() => {
        if (password.length === 0) {
            setPasswordIsValid(true);
            return;
        }

        setPasswordIsValid(password.length >= 8);
    }, [password]);

    return { password, setPassword, passwordIsValid };
};

export const useValidUsername = (initialValue: string,) => {
    const [username, setUsernameOut] = useState(initialValue);
    const [usernameIn, setUsername] = useState(initialValue);
    const [usernameIsValid, setUsernameIsValid] = useState(true);
    const [getEmail, { data, loading, error }] = useGetCaseSensitiveEmailLazyQuery();

    useEffect(() => {
        setUsernameOut(usernameIn);

        const emailDebounce = setTimeout(() => {
            if (usernameIn.length === 0) {
                setUsernameIsValid(true);
                setUsernameOut("");
                return;
            }

            const isValid = usernameIn.length >= 8;

            if (!isValid) {
                setUsernameIsValid(false);
                setUsernameOut(usernameIn);
                return;
            }

            getEmail({
                variables: {
                    email: usernameIn,
                }
            });
        }, 500);

        return () => {
            if (emailDebounce) {
                clearTimeout(emailDebounce);
            }
        };
    }, [usernameIn]);

    useEffect(
        () => {
            if (!data || loading) {
                return;
            }

            if (error) { // user does not exist
                setUsernameIsValid(true);
                setUsernameOut(usernameIn);
                return;
            }
            setUsernameOut(data.caseSensitiveEmail);
            setUsernameIsValid(true);
        },
        [data, loading]
    );


    return { username, setUsername, usernameIsValid };
};



export const useValidCode = (initialValue: string) => {
    const [code, setCode] = useState(initialValue);
    const [codeIsValid, setCodeIsValid] = useState(true);

    useEffect(() => {
        if (code.length === 0) {
            setCodeIsValid(true);
            return;
        }

        const isValid = code.length >= 6;

        setCodeIsValid(isValid);
    }, [code]);

    return { code, setCode, codeIsValid };
};

export const useWelcomeUser = (userId: string | undefined) => {
    const [username, setUsername] = useState<string>();
    const [fullName, setFullName] = useState<string>();

    const [loadUser, { data, loading }] = useGetWelcomeUnauthorizedUserLazyQuery();
    useEffect(
        () => {
            if (!data || loading) {
                return;
            }
            const user = data.welcomeUnauthorizedUser;
            setUsername(user.email);
            setFullName(user.firstName + " " + user.lastName);
        },
        [data, loading]
    );

    useEffect(
        () => {
            if (userId) {
                loadUser({
                    variables: {
                        userId: userId
                    }
                });
            }
        },
        [userId]
    );

    return { username, fullName };
};

